import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlusCircle, Search, Edit, Trash2, Eye, Calendar, User, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import AdminNavbar from '../components/admin/AdminNavbar';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { supabase, BlogPost } from '../utils/supabase';

const AdminPostsPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status && (status === 'all' || status === 'published' || status === 'draft')) {
      setStatusFilter(status as 'all' | 'published' | 'draft');
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    filterPosts();
  }, [posts, searchTerm, statusFilter]);

  const fetchPosts = async () => {
    try {
      setIsLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      console.log('[AdminPostsPage] Auth session:', {
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email
      });

      console.log('[AdminPostsPage] Fetching all posts (no status filter)');

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('[AdminPostsPage] Query result:', {
        data,
        error,
        errorDetails: error ? {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        } : null,
        postCount: data?.length,
        draftCount: data?.filter(p => p.status === 'draft').length,
        publishedCount: data?.filter(p => p.status === 'published').length
      });

      if (error) {
        console.error('[AdminPostsPage] Supabase error:', error);
        throw error;
      }

      console.log('[AdminPostsPage] All posts:', data);
      setPosts(data || []);
    } catch (error) {
      console.error('[AdminPostsPage] Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = posts;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(post => post.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(post =>
        (post.title || '').toLowerCase().includes(term) ||
        (post.excerpt || '').toLowerCase().includes(term) ||
        (post.author || '').toLowerCase().includes(term) ||
        (Array.isArray(post.tags) && post.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    setFilteredPosts(filtered);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPosts(posts.filter(post => post.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Manage Posts - Blog Admin"
        description="Manage blog posts"
        noIndex={true}
      />
      <AdminNavbar />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Blog Posts</h1>
              <p className="text-gray-600">Manage all your blog content</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchPosts()}
                disabled={isLoading}
                className="border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
              <Link
                to="/admin/posts/new"
                className="bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors flex items-center space-x-2 shadow-lg"
              >
                <PlusCircle size={20} />
                <span>New Post</span>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-11 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>

              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {filteredPosts.length} of {posts.length} posts
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-gray-600">
                  <span className="font-semibold text-green-600">{posts.filter(p => p.status === 'published').length}</span> Published
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600">
                  <span className="font-semibold text-yellow-600">{posts.filter(p => p.status === 'draft').length}</span> Drafts
                </span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Eye className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 mb-4">
                {posts.length === 0 ? 'No posts yet' : 'No posts match your filters'}
              </p>
              {posts.length === 0 && (
                <Link
                  to="/admin/posts/new"
                  className="inline-flex items-center space-x-2 text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                >
                  <PlusCircle size={20} />
                  <span>Create your first post</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredPosts.map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-48 h-48 md:h-auto flex-shrink-0">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h3>
                          <p className="text-gray-600 text-sm line-clamp-2 mb-3">{post.excerpt}</p>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <User size={16} className="mr-1" />
                              <span>{post.author}</span>
                            </div>
                            <div className="flex items-center">
                              <Calendar size={16} className="mr-1" />
                              <span>{formatDate(post.date)}</span>
                            </div>
                            <Link
                              to={`/admin/posts/${post.id}?focus=linkedin`}
                              className="flex items-center hover:opacity-80 transition-opacity group"
                              title={post.linkedin_url ? 'LinkedIn URL added' : 'Add LinkedIn URL'}
                            >
                              {post.linkedin_url ? (
                                <>
                                  <CheckCircle size={16} className="mr-1 text-green-600 group-hover:scale-110 transition-transform" />
                                  <span className="text-green-600 font-medium">LinkedIn</span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle size={16} className="mr-1 text-orange-500 group-hover:scale-110 transition-transform" />
                                  <span className="text-orange-500 font-medium">Missing</span>
                                </>
                              )}
                            </Link>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              post.status === 'published'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {post.status}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            {post.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                            {post.tags.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                +{post.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-100">
                        <Link
                          to={`/admin/posts/${post.id}`}
                          className="flex items-center space-x-2 px-4 py-2 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors"
                        >
                          <Edit size={16} />
                          <span>Edit</span>
                        </Link>

                        {post.status === 'published' && (
                          <Link
                            to={`/blog/${post.slug}`}
                            target="_blank"
                            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <Eye size={16} />
                            <span>View</span>
                          </Link>
                        )}

                        {deleteConfirm === post.id ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                            >
                              Confirm Delete
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(post.id)}
                            className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={16} />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPostsPage;
